'use client';

import type { Libraries } from '@react-google-maps/api';
import { useLoadScript } from '@react-google-maps/api';
import type { FormEvent, ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FieldErrors, FieldValues, Path, PathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { Input } from '../form/Input';
import type { LabelVariantsProps } from '../form/Input/styles';

type InputIntent = 'search' | 'secondary' | 'primary' | 'old' | 'primaryWhite' | 'otp' | 'payment';
interface Props<T extends FieldValues> {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  onInputChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  label?: ReactNode;
  labelStyle?: LabelVariantsProps;
  placeholder?: string;
  name: Path<T>;
  value?: string | number;
  errors?: FieldErrors<T>;
  required?: boolean;
  autoComplete?: string;
  autocompleteOptions?: google.maps.places.AutocompleteOptions;
  disabled?: boolean;
  helperText?: string;
  intent?: InputIntent;
}

export const PlaceSelect = <T extends FieldValues>({
  onPlaceSelect,
  onInputChange,
  onBlur,
  label,
  labelStyle,
  name,
  errors,
  disabled,
  value,
  placeholder,
  required,
  autoComplete,
  helperText,
  intent,
  autocompleteOptions = {
    fields: ['formatted_address', 'geometry', 'name'],
  },
}: Props<T>) => {
  const LIBRARIES = useMemo(() => ['places'] as Libraries, []);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const formContext = useFormContext<T>();
  const setValue = formContext?.setValue ?? (() => {});

  const handleInput = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      onInputChange?.((event.target as HTMLInputElement).value);
    },
    [onInputChange]
  );

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY as string,
    libraries: LIBRARIES,
  });

  const initMap = useCallback(async () => {
    const el = inputRef.current;
    if (!el || !(el instanceof HTMLInputElement)) return;

    await google.maps.importLibrary('places');
    const autocomplete = new google.maps.places.Autocomplete(el, autocompleteOptions);
    autocompleteRef.current = autocomplete;

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      onPlaceSelect(place);
      if (place?.formatted_address) el.blur();
    });
  }, [onPlaceSelect, autocompleteOptions]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;
    initMap();
  }, [isLoaded, initMap]);

  return (
    <Input
      ref={inputRef}
      className="w-full"
      required={required}
      name={name}
      value={value}
      onInput={handleInput}
      placeholder={placeholder}
      label={label}
      labelStyle={labelStyle}
      errors={errors}
      disabled={disabled}
      helperText={helperText}
      autoComplete={autoComplete}
      intent={intent}
      onBlur={(e) => {
        setValue(name as Path<T>, e.currentTarget.value as PathValue<T, Path<T>>, {
          shouldDirty: true,
          shouldValidate: true,
        });
        onBlur?.(e);
      }}
    />
  );
};
